<template>
  <div class="vue-stepper-wrapper">
    <div class="vue-stepper-header" v-if="title">
      <h5 class="fw-semibold mb-0">{{ title }}</h5>
      <small class="text-muted">{{ subTitle }}</small>
    </div>

    <div class="vue-stepper-viewport" ref="viewport">
      <div class="vue-stepper-track" ref="track">
        <div
          v-for="(step, index) in steps"
          :key="step.key"
          class="vue-stepper-item"
          :data-key="step.key"
          :title="step.tooltip"
          :class="{
            active: step.key === activeKey,
            completed: completedKeys.includes(step.key),
            disabled:
              !completedKeys.includes(step.key) && step.key !== currentStepKey,
          }"
          @click="handleStepClick(step)"
        >
          <div
            v-if="index < steps.length - 1"
            class="vue-stepper-connector"
            :class="{
              'bg-success':
                completedKeys.includes(steps[index + 1].key) ||
                steps[index + 1].key === currentStepKey,
              'bg-secondary':
                !completedKeys.includes(steps[index + 1].key) &&
                steps[index + 1].key !== currentStepKey,
            }"
          ></div>

          <div
            class="vue-stepper-circle d-flex align-items-center justify-content-center mx-auto"
            :class="{
              'bg-success text-white': completedKeys.includes(step.key),
              'bg-warning text-white':
                step.key === currentStepKey && !completeAll,
              'bg-secondary text-white':
                !completedKeys.includes(step.key) &&
                step.key !== currentStepKey,
              'vue-active-ring': step.key === activeKey,
            }"
          >
            <i v-if="completedKeys.includes(step.key)" class="fas fa-check"></i>
            <i v-else :class="step.icon"></i>
          </div>

          <div
            class="mt-2 font-weight-bold text-center vue-stepper-label"
            :class="step.key === activeKey ? 'text-primary' : 'text-secondary'"
          >
            {{ step.label }}
          </div>
        </div>
      </div>
    </div>

    <transition name="vue-fade">
      <div
        v-if="activeKey"
        class="vue-stepper-content border rounded bg-white shadow-sm"
      >
        <slot :name="activeKey" />
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "ProgressStepper",

  model: {
    prop: "currentStepKey",
    event: "change",
  },

  props: {
    title: String,
    subTitle: String,
    steps: {
      type: Array,
      required: true,
    },
    currentStepKey: {
      type: String,
      default: "",
    },
    completeAll: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeKey: this.currentStepKey || (this.steps[0] && this.steps[0].key),
      completedKeys: [],
    };
  },

  watch: {
    currentStepKey: {
      immediate: true,
      handler(newKey) {
        if (this.completeAll) {
          this.completedKeys = this.steps.map((s) => s.key);

          this.activeKey = this.steps[this.steps.length - 1].key;
        } else {
          this.markCompletedUpTo(newKey);
          this.activeKey = newKey;
        }

        this.$nextTick(() => this.scrollToActive(this.activeKey));
      },
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.adjustAlignment();
      this.scrollToActive(this.activeKey);
      window.addEventListener("resize", this.adjustAlignment);
    });
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.adjustAlignment);
  },
  methods: {
    handleStepClick(step) {
      if (
        this.completedKeys.includes(step.key) ||
        step.key === this.currentStepKey
      ) {
        this.activeKey = step.key;
        this.$nextTick(() => this.scrollToActive(step.key));
      }
    },
    markCompletedUpTo(key) {
      const idx = this.steps.findIndex((s) => s.key === key);
      if (idx >= 0) {
        const newCompleted = this.steps.slice(0, idx).map((s) => s.key);
        this.completedKeys = [
          ...new Set([...this.completedKeys, ...newCompleted]),
        ];
      }
    },
    scrollToActive(key) {
      const track = this.$refs.track;
      if (!track) return;
      const el = track.querySelector(`[data-key="${key}"]`);
      if (!el) return;
      const viewport = this.$refs.viewport;
      const elLeft = el.offsetLeft;
      const elRight = elLeft + el.offsetWidth;
      const viewportRight = viewport.scrollLeft + viewport.clientWidth;

      if (elLeft < viewport.scrollLeft || elRight > viewportRight) {
        viewport.scrollTo({
          left: elLeft - viewport.clientWidth / 2 + el.offsetWidth / 2,
          behavior: "smooth",
        });
      }
    },
    adjustAlignment() {
      const viewport = this.$refs.viewport;
      const track = this.$refs.track;
      if (!viewport || !track) return;

      if (track.scrollWidth <= viewport.clientWidth) {
        track.style.justifyContent = "center";
      } else {
        track.style.justifyContent = "flex-start";
      }
    },
  },
};
</script>

<style scoped>
.vue-stepper-wrapper {
  width: 100%;
  background: #f9fafb;
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.vue-stepper-header {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
  text-align: center;
}

.vue-stepper-viewport {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  touch-action: pan-x;
  padding: 10px 0;
}
.vue-stepper-viewport::-webkit-scrollbar {
  display: none;
}

.vue-stepper-track {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: max-content;
  padding: 0 10px;
}

.vue-stepper-item {
  position: relative;
  text-align: center;
  flex: 0 0 auto;
  min-width: 150px;
  cursor: pointer;
  transition: opacity 0.3s ease;
}
.vue-stepper-item.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.vue-stepper-item:last-child .vue-stepper-connector {
  display: none;
}

.vue-stepper-circle {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  border: 4px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  font-size: 1.3rem;
  z-index: 2;
  margin: 7px auto;
  transition: all 0.25s ease;
}
.vue-stepper-circle:hover {
  transform: scale(1.05);
}
.vue-active-ring {
  box-shadow: 0 0 0 5px rgba(13, 110, 253, 0.25);
}

.vue-stepper-connector {
  position: absolute;
  top: 40%;
  left: calc(50% + 28px);
  height: 4px;
  width: calc(100% - 56px);
  transform: translateY(-50%);
  border-radius: 4px;
  z-index: 1;
  transition: background 0.3s ease;
}

.vue-stepper-content {
  width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem 1.25rem;
  font-size: 0.95rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.vue-fade-enter-active,
.vue-fade-leave-active {
  transition: opacity 0.3s ease;
}
.vue-fade-enter-from,
.vue-fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .vue-stepper-item {
    min-width: 120px;
  }
  .vue-stepper-circle {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
  .vue-stepper-label {
    font-size: 1rem;
    white-space: nowrap;
  }
}
</style>
